import browser from '@openclassrooms/utils/browser';

let isMathjaxLoading = false;
type MathJaxStartup = {
  promise: Promise<void>;
};

type MathJaxInstance = {
  startup: MathJaxStartup;
  typesetPromise: (elements: Element[]) => Promise<void>;
};

type MathJaxCandidate = {
  startup?: Partial<MathJaxStartup>;
  typesetPromise?: MathJaxInstance['typesetPromise'];
};

type MathJaxLoadingPromiseResolver = {
  resolve: (mathJax: MathJaxInstance) => void;
  reject: (reason?: unknown) => void;
};

const mathjaxLoadingPromisesResolvers: MathJaxLoadingPromiseResolver[] = [];

const MATHJAX_VERSION = '4.1.3';
const MATHJAX_PATH = `https://cdnjs.cloudflare.com/ajax/libs/mathjax/${MATHJAX_VERSION}/tex-svg.js`;
const MATHJAX_INITIALIZATION_ERROR = 'MathJax failed to initialize';
const LEGACY_ARRAY_PREAMBLE_PATTERN = /\\begin\{array\}\[([lcr])\]\s*0/g;
const STANDARD_COLOR_GROUP_PATTERN = /\{\\color\{([^{}]+)\}/g;

function normalizeLegacyTex(data: { math: { math: string } }) {
  const { math } = data;
  math.math = math.math
    .replace(LEGACY_ARRAY_PREAMBLE_PATTERN, '\\begin{array}{$1}')
    .replace(STANDARD_COLOR_GROUP_PATTERN, '\\color{$1}{');
}

const MATHJAX_CONFIGURATION = {
  loader: {
    load: ['[tex]/noerrors'],
  },
  startup: {
    typeset: false,
  },
  output: {
    linebreaks: {
      inline: false,
    },
  },
  tex: {
    packages: { '[+]': ['noerrors'] },
    autoload: {
      color: [],
      colorv2: ['color'],
    },
    inlineMath: [
      ['\\(', '\\)'],
      ['$\\(', '\\)$'],
    ],
    displayMath: [
      ['$$', '$$'],
      ['\\[', '\\]'],
      ['$\\[', '\\]$'],
    ],
    preFilters: [normalizeLegacyTex],
    processEscapes: false,
  },
  options: {
    enableMenu: false,
  },
};

function getMathJax() {
  return (browser.window as typeof window & { MathJax?: MathJaxCandidate }).MathJax;
}

function hasMathjaxStartup(
  mathJax: MathJaxCandidate | undefined
): mathJax is MathJaxCandidate & { startup: MathJaxStartup } {
  return Boolean(mathJax?.startup?.promise);
}

function isMathjaxReady(
  mathJax: MathJaxCandidate | undefined = getMathJax()
): mathJax is MathJaxInstance {
  return Boolean(hasMathjaxStartup(mathJax) && mathJax.typesetPromise);
}

function waitForMathjaxStartup(mathJax: MathJaxCandidate) {
  if (!hasMathjaxStartup(mathJax)) {
    return Promise.reject(new Error(MATHJAX_INITIALIZATION_ERROR));
  }

  return mathJax.startup.promise.then(() => {
    const initializedMathJax = getMathJax();
    if (!isMathjaxReady(initializedMathJax)) {
      throw new Error(MATHJAX_INITIALIZATION_ERROR);
    }

    return initializedMathJax;
  });
}

function resolveMathjaxLoading(mathJax: MathJaxInstance) {
  const resolvers = mathjaxLoadingPromisesResolvers.splice(0);
  isMathjaxLoading = false;
  resolvers.forEach(({ resolve }) => resolve(mathJax));
}

function rejectMathjaxLoading(reason: unknown) {
  const resolvers = mathjaxLoadingPromisesResolvers.splice(0);
  isMathjaxLoading = false;
  resolvers.forEach(({ reject }) => reject(reason));
}

function getInstance() {
  const mathJax = getMathJax();
  if (mathJax && hasMathjaxStartup(mathJax)) {
    return waitForMathjaxStartup(mathJax);
  }

  return new Promise<MathJaxInstance>((resolve, reject) => {
    mathjaxLoadingPromisesResolvers.push({ resolve, reject });
    if (isMathjaxLoading) return;
    isMathjaxLoading = true;

    (browser.window as typeof window & { MathJax: typeof MATHJAX_CONFIGURATION }).MathJax =
      MATHJAX_CONFIGURATION;

    browser.loadScript({
      src: MATHJAX_PATH,
      onLoaded: () => {
        const loadedMathJax = getMathJax();
        if (!loadedMathJax) {
          rejectMathjaxLoading(new Error(MATHJAX_INITIALIZATION_ERROR));
          return;
        }

        waitForMathjaxStartup(loadedMathJax).then(resolveMathjaxLoading, rejectMathjaxLoading);
      },
    });
  });
}

function highlight(domElements: Element | Element[]) {
  const elements = Array.isArray(domElements) ? domElements : [domElements];

  return getInstance().then((mathJax) => mathJax.typesetPromise(elements));
}

export default { mathjax: { isMathjaxReady, getInstance, highlight } };
